import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import {
  useDashboardStats,
  useCoachTeam,
  useTeamMembers,
  useRemoveMember,
  useRegenerateInviteCode,
  useUpdateTeam,
} from '@/hooks/use-coach';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Users,
  CreditCard,
  Settings,
  LayoutDashboard,
  Copy,
  RefreshCw,
  UserX,
  ChevronRight,
  Check,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics/posthog';

export default function CoachDashboard() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: teams } = useCoachTeam();
  const team = teams?.[0];
  const { data: members, isLoading: membersLoading } = useTeamMembers(team?.id);

  const removeMemberMutation = useRemoveMember();
  const regenerateCodeMutation = useRegenerateInviteCode();
  const updateTeamMutation = useUpdateTeam();

  const [copiedCode, setCopiedCode] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: number; name: string } | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }

    if (!isLoading && user?.accountType !== 'coach') {
      setLocation('/');
      return;
    }
  }, [isAuthenticated, isLoading, user, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== 'coach') {
    return null;
  }

  const copyInviteCode = () => {
    if (stats?.inviteCode) {
      navigator.clipboard.writeText(stats.inviteCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast({
        title: 'Copied!',
        description: 'Invite code copied to clipboard.',
      });
    }
  };

  const handleRemoveMember = () => {
    if (memberToRemove && team) {
      removeMemberMutation.mutate(
        { teamId: team.id, memberId: memberToRemove.id },
        {
          onSuccess: () => {
            trackEvent('coach_member_removed', {
              team_id: team.id,
              member_id: memberToRemove.id,
            });
          },
        }
      );
      setMemberToRemove(null);
    }
  };

  const handleRegenerateCode = () => {
    if (team) {
      regenerateCodeMutation.mutate(team.id);
    }
  };

  const handleUpdateTeamName = () => {
    if (team && newTeamName.trim()) {
      updateTeamMutation.mutate({
        teamId: team.id,
        updates: { name: newTeamName.trim() },
      });
      setEditingName(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  // If no team exists, show create team prompt
  if (!statsLoading && !stats?.hasTeam) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-gray-200 dark:bg-[var(--dark-3)] rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Create Your Team</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              You don't have a team yet. Create one to start managing your athletes.
            </p>
            <Button className="btn-primary px-8 py-3 rounded-2xl" onClick={() => setLocation('/create-team')}>
              Create Team
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">{stats?.teamName || 'Team Dashboard'}</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage your team, track progress, and support your athletes.
            </p>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid grid-cols-3 w-full max-w-2xl glass-card dark:glass-card-dark rounded-2xl p-1">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="players" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Players</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Total Players</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats?.totalMembers || 0}</div>
                  </CardContent>
                </Card>

                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Credits Remaining</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[var(--apple-green)]">{stats?.creditsRemaining || 0}</div>
                  </CardContent>
                </Card>

                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Credits Used This Month</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats?.creditsUsedThisMonth || 0}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Invite Code Card */}
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Invite Code</CardTitle>
                  <CardDescription>Share this code with your athletes to join your team</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="glass-card dark:glass-card-dark px-6 py-3 rounded-2xl font-mono text-2xl tracking-wider">
                      {stats?.inviteCode || '--------'}
                    </div>
                    <Button variant="outline" size="icon" onClick={copyInviteCode}>
                      {copiedCode ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRegenerateCode}
                      disabled={regenerateCodeMutation.isPending}
                    >
                      <RefreshCw className={`w-4 h-4 ${regenerateCodeMutation.isPending ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Players Tab */}
            <TabsContent value="players" className="space-y-4">
              {membersLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Loading players...</p>
                </div>
              ) : !members || members.length === 0 ? (
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Players Yet</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Share your invite code with athletes to build your team.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <Card key={member.id} className="glass-card dark:glass-card-dark rounded-3xl shadow-lg card-hover transition-all duration-300">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] flex items-center justify-center text-white font-bold">
                              {member.user.name?.[0]?.toUpperCase() || member.user.email[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold">{member.user.name || 'Unnamed Player'}</p>
                              <p className="text-sm text-gray-500">{member.user.email}</p>
                              <p className="text-xs text-gray-400">Joined {formatDate(member.joinedAt.toString())}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setLocation(`/coach/players/${member.userId}`)}>
                              View <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20"
                              onClick={() => setMemberToRemove({ id: member.id, name: member.user.name || 'this player' })}
                            >
                              <UserX className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4">
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                <CardHeader>
                  <CardTitle>Team Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Team Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Team Name</label>
                    {editingName ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="Enter team name"
                          className="max-w-xs"
                        />
                        <Button onClick={handleUpdateTeamName} disabled={updateTeamMutation.isPending}>
                          Save
                        </Button>
                        <Button variant="ghost" onClick={() => setEditingName(false)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold">{stats?.teamName || 'Unnamed Team'}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNewTeamName(stats?.teamName || '');
                            setEditingName(true);
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Invite Code Management */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Invite Code</label>
                    <div className="flex items-center gap-4">
                      <code className="glass-card dark:glass-card-dark px-4 py-2 rounded-2xl font-mono">
                        {stats?.inviteCode || '--------'}
                      </code>
                      <Button
                        variant="outline"
                        onClick={handleRegenerateCode}
                        disabled={regenerateCodeMutation.isPending}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${regenerateCodeMutation.isPending ? 'animate-spin' : ''}`} />
                        Regenerate
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Regenerating the code will invalidate the previous one.
                    </p>
                  </div>

                  {/* Subscription Status */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Subscription</label>
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[var(--apple-green)]" />
                      <span>
                        {user?.planTier === 'free' && 'Free Plan'}
                        {user?.planTier === 'pro_starter' && 'Pro Starter Plan'}
                        {user?.planTier === 'elite_pro' && 'Elite Pro Plan'}
                        {user?.planTier === 'ultra_team' && 'Ultra Team Plan'}
                        {!user?.planTier && 'Free Plan'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {stats?.creditsRemaining || 0} credits remaining this month
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent className="glass-card dark:glass-card-dark rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Player</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.name} from your team?
              They can rejoin later using the invite code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600 rounded-2xl"
              onClick={handleRemoveMember}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
